<template>
  <div class="business-details-card">
    <!-- Cover Image Section -->
    <div class="business-cover-image">
      <img v-if="business.cover_image" :src="business.cover_image" alt="Cover Image" class="cover-img">
      <img v-else src="img/noimage.png" alt="No Image" class="cover-img">
    </div>

    <!-- Info Sections -->
    <div class="business-info-container">
      <!-- Basic Info Section -->
      <div class="info-section">
        <div class="section-title">
          <i class="ni ni-single-02 mr-2 text-primary"></i>
          <span>Basic Information</span>
        </div>
        <div class="section-content">
          <div class="info-item">
            <div class="info-label">Name:</div>
            <div class="info-value">{{ business.business_name_english }}</div>
          </div>
          
          <div v-if="business.business_name_arabic" class="info-item">
            <div class="info-label">Arabic Name:</div>
            <div class="info-value" style="direction: rtl">{{ business.business_name_arabic }}</div>
          </div>
          
          <div class="info-item">
            <div class="info-label">Business ID:</div>
            <div class="info-value">{{ business._id }}</div>
          </div>
          
          <div class="info-item">
            <div class="info-label">Status:</div>
            <div class="info-value">
              <span 
                class="badge badge-pill" 
                :class="{
                  'badge-success': business.status === 'approved',
                  'badge-warning': business.status === 'pending',
                  'badge-danger': business.status === 'rejected'
                }"
              >
                {{ business.status }}
              </span>
            </div>
          </div>
        </div>
      </div>

      <!-- Categories Section -->
      <div class="info-section">
        <div class="section-title">
          <i class="ni ni-tag mr-2 text-primary"></i>
          <span>Categories & Subcategories</span>
        </div>
        <div class="section-content">
          <!-- Categories (English) -->
          <div 
            v-if="business.business_category && business.business_category.length" 
            class="info-item"
          >
            <div class="info-label">Categories:</div>
            <div class="info-value">
              <span 
                v-for="category in business.business_category" 
                :key="category._id" 
                class="badge badge-pill badge-light-primary category-item"
              >
                {{ category.business_category_name }}
              </span>
            </div>
          </div>
            <!-- Categories (Arabic) -->
          <div 
            v-if="business.business_category && business.business_category.length" 
            class="info-item"
          >
            <div class="info-label">Categories (AR):</div>
            <div class="info-value">
              <span 
                v-for="category in business.business_category" 
                :key="'ar-'+category._id" 
                class="badge badge-pill badge-light-primary category-item"
              >
                {{ category.business_category_name_ar }}
              </span>
            </div>
          </div>

          <!-- Subcategories (English) -->
          <div 
            v-if="business.business_subcategory && business.business_subcategory.length" 
            class="info-item"
          >
            <div class="info-label">Subcategories:</div>
            <div class="info-value">
              <span 
                v-for="subcategory in business.business_subcategory" 
                :key="subcategory._id" 
                class="badge badge-pill badge-light-info category-item"
              >
                {{ subcategory.business_subcategory_name }}
              </span>
            </div>
          </div>
          
          <!-- Subcategories (Arabic) -->
          <div 
            v-if="business.business_subcategory && business.business_subcategory.length" 
            class="info-item"
          >
            <div class="info-label">Subcategories (AR):</div>
            <div class="info-value">
              <span 
                v-for="subcategory in business.business_subcategory" 
                :key="'ar-'+subcategory._id" 
                class="badge badge-pill badge-light-info category-item"
              >
                {{ subcategory.business_subcategory_name_ar }}
              </span>
            </div>
          </div>
        </div>
      </div>

      <!-- Description Section -->
      <div class="info-section" v-if="business.business_description_english || business.business_description_arabic">
        <div class="section-title">
          <i class="ni ni-align-left-2 mr-2 text-primary"></i>
          <span>Description</span>
        </div>
        <div class="section-content">
          <div v-if="business.business_description_english" class="info-item">
            <div class="info-value description-text">{{business.business_description_english}}</div>
          </div>
          <div v-if="business.business_description_arabic" class="info-item mt-2">
            <div class="info-label">Arabic:</div>
            <div class="info-value description-text">{{business.business_description_arabic}}</div>
          </div>
        </div>
      </div>

      <!-- Contact Section -->
      <div class="info-section">
        <div class="section-title">
          <i class="ni ni-mobile-button mr-2 text-primary"></i>
          <span>Contact Information</span>
        </div>
        <div class="section-content">
          <div v-if="business.phone" class="info-item">
            <div class="info-label">Phone:</div>
            <div class="info-value">{{business.phone}}</div>
          </div>
          <div v-if="business.email" class="info-item">
            <div class="info-label">Email:</div>
            <div class="info-value">{{business.email}}</div>
          </div>
          <div v-if="business.address" class="info-item">
            <div class="info-label">Address:</div>
            <div class="info-value">{{business.address}}</div>
          </div>
          <div v-if="business.address_ar" class="info-item">
            <div class="info-label">Address (AR):</div>
            <div class="info-value">{{business.address_ar}}</div>
          </div>
          <div v-if="business.location_lat && business.location_long" class="info-item">
            <a 
              :href="'https://www.google.com/maps/search/?api=1&query='+business.location_lat+','+business.location_long" 
              target="_blank"
              class="btn btn-sm btn-outline-info btn-block"
            >
              <i class="ni ni-pin-3 mr-1"></i> View on Map
            </a>
          </div>
        </div>
      </div>
      
      <!-- Social Links Section -->
      <div class="info-section">
        <div class="section-title">
          <i class="ni ni-world-2 mr-2 text-primary"></i>
          <span>Connect</span>
        </div>
        <div class="section-content">
          <div class="d-flex flex-wrap">
            <a @click="$emit('copy-link', business._id)" class="social-btn invite-link mr-2 mb-2">
              <i class="fa fa-link"></i>
            </a>
            <a v-if="business.fb_link && business.fb_link!=' '" target="_blank" :href="'https://www.facebook.com/'+business.fb_link" class="social-btn facebook mr-2 mb-2">
              <i class="fa fa-facebook"></i>
            </a>
            <a v-if="business.twitter_link && business.twitter_link!=' '" target="_blank" :href="'https://www.twitter.com/'+business.twitter_link" class="social-btn twitter mr-2 mb-2">
              <i class="fa fa-twitter"></i>
            </a>
            <a v-if="business.instagram_link && business.instagram_link!=' '" target="_blank" :href="'https://www.instagram.com/'+business.instagram_link" class="social-btn instagram mr-2 mb-2">
              <i class="fa fa-instagram"></i>
            </a>
            <a v-if="business.web_link && business.web_link!=' '" target="_blank" :href="'http://'+business.web_link" class="social-btn website mr-2 mb-2">
              <i class="fa fa-globe"></i>
            </a>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  name: 'BusinessDetails',
  props: {
    business: {
      type: Object,
      required: true
    }
  }
}
</script>

<style scoped>
.business-details-card {
  background-color: #fff;
  border-radius: 15px;
  box-shadow: 0 0 20px rgba(0, 0, 0, 0.05);
  margin-top: -50px;
  position: relative;
  overflow: hidden;
}

.business-cover-image {
  height: 200px;
  overflow: hidden;
  position: relative;
}

.cover-img {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.business-info-container {
  padding: 20px;
}

.info-section {
  margin-bottom: 20px;
  padding-bottom: 20px;
  border-bottom: 1px solid #f5f5f5;
}

.info-section:last-child {
  margin-bottom: 0;
  padding-bottom: 0;
  border-bottom: none;
}

.section-title {
  display: flex;
  align-items: center;
  font-weight: 600;
  margin-bottom: 15px;
  color: #344767;
  font-size: 1rem;
}

.info-item {
  margin-bottom: 8px;
}

.info-item:last-child {
  margin-bottom: 0;
}

.info-label {
  font-weight: 600;
  color: #8898aa;
  margin-bottom: 3px;
  font-size: 0.875rem;
}

.info-value {
  color: #344767;
}

.description-text {
  line-height: 1.6;
  color: #525f7f;
}

.badge-light-primary {
  background-color: rgba(94, 114, 228, 0.1);
  color: #5e72e4;
}

.badge-light-info {
  background-color: rgba(17, 205, 239, 0.1);
  color: #11cdef;
}

.category-item {
  margin-right: 5px;
  margin-bottom: 5px;
  font-size: 0.8rem;
  font-weight: 500;
  padding: 0.35rem 0.65rem;
}

/* Social Links */
.social-btn {
  width: 36px;
  height: 36px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  border-radius: 50%;
  color: #fff;
  transition: all 0.2s ease;
  cursor: pointer;
}

.social-btn:hover {
  transform: translateY(-2px);
  box-shadow: 0 4px 10px rgba(0, 0, 0, 0.15);
}

.social-btn.invite-link {
  background-color: #5e72e4;
}

.social-btn.facebook {
  background-color: #3b5998;
}

.social-btn.twitter {
  background-color: #1da1f2;
}

.social-btn.instagram {
  background: linear-gradient(45deg, #f09433 0%, #e6683c 25%, #dc2743 50%, #cc2366 75%, #bc1888 100%);
}

.social-btn.website {
  background-color: #4285f4;
}
</style>
</style>
